/**
 * Ocean Booking Transport Plan Fields Component
 *
 * Renders the transport plan (response data) section of the Ocean Booking form.
 * Each transport stage contains: stageType, vesselName, vesselIMO, voyageNumber,
 * loadLocation, dischargeLocation, estimatedDeparture, estimatedArrival.
 *
 * Stage type labels:
 * - PRC = Pre-carriage
 * - MNC = Main Carriage
 * - ONC = On-carriage
 *
 * Also renders shipmentCutOffTimes and feedbacks if present.
 */

import { useFieldArray } from 'react-hook-form';
import Input from '../common/Input';
import Button from '../common/Button';
import { getFieldIssues } from '../../utils/confidenceUtils';

/**
 * Transport stage type options
 */
const STAGE_TYPES = [
  { value: 'PRC', label: 'PRC - Pre-carriage' },
  { value: 'MNC', label: 'MNC - Main Carriage' },
  { value: 'ONC', label: 'ONC - On-carriage' }
];

/**
 * Map stage type codes to readable labels
 */
const STAGE_TYPE_LABELS = {
  PRC: 'Pre-carriage',
  MNC: 'Main Carriage',
  ONC: 'On-carriage'
};

/**
 * OBTransportFields Component
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control
 * @param {Function} props.register - react-hook-form register
 * @param {boolean} props.isEditMode - Whether fields are editable
 * @param {Array} props.issues - Unified issues array
 * @param {Array} [props.shipmentCutOffTimes] - Cut-off time data (read-only)
 * @param {Array} [props.feedbacks] - Feedback entries (read-only)
 */
function OBTransportFields({ control, register, isEditMode, issues = [], shipmentCutOffTimes = [], feedbacks = [] }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'transportPlan'
  });

  const addStage = () => {
    append({
      stageType: 'MNC',
      vesselName: '',
      vesselIMO: '',
      voyageNumber: '',
      loadLocation: '',
      dischargeLocation: '',
      estimatedDeparture: '',
      estimatedArrival: ''
    });
  };

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-lg font-medium text-gray-900">
          Transport Plan
        </h3>
        {isEditMode && (
          <Button variant="secondary" size="sm" onClick={addStage}>
            + Add Stage
          </Button>
        )}
      </div>

      {/* Empty state */}
      {fields.length === 0 && (
        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
          No transport plan available.
        </div>
      )}

      {/* Transport stages */}
      {fields.map((field, index) => (
        <div
          key={field.id}
          className="border border-gray-200 rounded-lg p-4 space-y-3"
        >
          {/* Stage header */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {STAGE_TYPE_LABELS[field.stageType] || `Stage #${index + 1}`}
            </span>
            {isEditMode && (
              <button
                type="button"
                onClick={() => remove(index)}
                className="text-red-500 hover:text-red-700 text-sm"
              >
                Remove
              </button>
            )}
          </div>

          {/* Stage fields grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Stage Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Stage Type
              </label>
              <select
                {...register(`transportPlan.${index}.stageType`)}
                disabled={!isEditMode}
                className={`w-full px-3 py-2 border rounded-md shadow-sm text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${!isEditMode ? 'bg-gray-50 text-gray-600' : ''}`}
              >
                {STAGE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            {/* Vessel Name */}
            <Input
              label="Vessel Name"
              {...register(`transportPlan.${index}.vesselName`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `transportPlan[${index}].vesselName`)}
            />

            {/* Vessel IMO */}
            <Input
              label="Vessel IMO"
              {...register(`transportPlan.${index}.vesselIMO`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `transportPlan[${index}].vesselIMO`)}
              placeholder="e.g., 9321483"
            />

            {/* Voyage Number */}
            <Input
              label="Voyage Number"
              {...register(`transportPlan.${index}.voyageNumber`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `transportPlan[${index}].voyageNumber`)}
            />

            {/* Load Location */}
            <Input
              label="Load Location"
              {...register(`transportPlan.${index}.loadLocation`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `transportPlan[${index}].loadLocation`)}
              placeholder="Port or location code"
            />

            {/* Discharge Location */}
            <Input
              label="Discharge Location"
              {...register(`transportPlan.${index}.dischargeLocation`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `transportPlan[${index}].dischargeLocation`)}
              placeholder="Port or location code"
            />

            {/* Estimated Departure */}
            <Input
              label="Estimated Departure"
              type="date"
              {...register(`transportPlan.${index}.estimatedDeparture`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `transportPlan[${index}].estimatedDeparture`)}
            />

            {/* Estimated Arrival */}
            <Input
              label="Estimated Arrival"
              type="date"
              {...register(`transportPlan.${index}.estimatedArrival`)}
              disabled={!isEditMode}
              issues={getFieldIssues(issues, `transportPlan[${index}].estimatedArrival`)}
            />
          </div>
        </div>
      ))}

      {/* Shipment Cut-Off Times (read-only) */}
      {shipmentCutOffTimes.length > 0 && (
        <div className="mt-6">
          <h4 className="text-md font-medium text-gray-900 border-b pb-2 mb-3">
            Shipment Cut-Off Times
          </h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cut-Off Type</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cut-Off Date/Time</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {shipmentCutOffTimes.map((cutOff, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {cutOff.cutOffDateTimeCode || cutOff.type || '-'}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {cutOff.cutOffDateTime || cutOff.dateTime || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Feedbacks (read-only) */}
      {feedbacks.length > 0 && (
        <div className="mt-6">
          <h4 className="text-md font-medium text-gray-900 border-b pb-2 mb-3">
            Feedbacks
          </h4>
          <div className="space-y-2">
            {feedbacks.map((fb, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <div className="flex items-start gap-3">
                  {fb.severity && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      fb.severity === 'ERROR' ? 'bg-red-100 text-red-700' :
                      fb.severity === 'WARNING' ? 'bg-amber-100 text-amber-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {fb.severity}
                    </span>
                  )}
                  <div className="flex-1">
                    {fb.code && (
                      <p className="text-xs font-medium text-gray-500">{fb.code}</p>
                    )}
                    <p className="text-sm text-gray-700">{fb.message || fb.detail || '-'}</p>
                    {fb.updatedDateTime && (
                      <p className="text-xs text-gray-400 mt-1">{fb.updatedDateTime}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default OBTransportFields;
